import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { ADMIN_HOME_PATH, CONSIGNOR_LANDING_PATH, HOME_PATH } from '../routes/routePaths';
import urbanImage from '../assets/urban_logo.png';
import shotHome from '../assets/landing-home.png';
import shotInventory from '../assets/landing-inventory.png';
import shotSales from '../assets/landing-sales.png';
import shotPayments from '../assets/landing-payments.png';
import storeShot1 from '../assets/landing-store-1.png';
import storeShot2 from '../assets/landing-store-2.png';
import storeShot3 from '../assets/landing-store-3.png';
import heroShot from '../assets/landing-hero.jpg';
import styles from './ConsignorLanding.module.css';

const CDN = 'https://cdn.shopify.com/s/files/1/0701/1349/3272/files';

const IMAGES = {
  zebra: `${CDN}/Catalogo_Edit_2_-_2025-06-18T184426.139.png?v=1750293898&width=550`,
  grail: `${CDN}/grail-pfti.png?width=900`,
  kaws: `${CDN}/kaws-pfti.png?width=900`,
  accessories: `${CDN}/accesorios_2-pfti.png?width=900`,
};

function ConsignorLanding(): React.ReactElement {
  const { isAuthenticated, user } = useUser();
  const portalPath = user?.isAdmin ? ADMIN_HOME_PATH : HOME_PATH;
  const primaryTo = isAuthenticated ? portalPath : '/create-account';
  const primaryLabel = isAuthenticated ? 'Ir al portal' : 'Empezar a consignar';

  React.useEffect(() => {
    document.title = 'Consignar · Urban Utility';
    return () => {
      document.title = 'Urban Utility';
    };
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.ticker}>
        <span>Guadalajara</span>
        <span>Sneakers · Streetwear · Grails</span>
        <span>Tienda · Web · POS</span>
        <span>Avisos por WhatsApp</span>
      </div>

      <header className={styles.nav}>
        <Link to={CONSIGNOR_LANDING_PATH} aria-label="Urban Utility">
          <img className={styles.logo} src={urbanImage} alt="Urban Utility" />
        </Link>
        <nav className={styles.navLinks}>
          <a className={styles.navGhost} href="#portal">
            El portal
          </a>
          <a className={styles.navGhost} href="#proceso">
            Cómo
          </a>
          {!isAuthenticated && (
            <Link className={styles.navGhost} to="/login">
              Entrar
            </Link>
          )}
          <Link className={styles.navSolid} to={primaryTo}>
            {primaryLabel}
          </Link>
        </nav>
      </header>

      <section className={styles.hero}>
        <div>
          <p className={styles.kicker}>Consigna · Guadalajara</p>
          <h1 className={styles.headline}>
            Tus piezas.
            <br />
            Nuestros
            <br />
            canales.
          </h1>
          <p className={styles.lede}>
            Te ayudamos a vender tus artículos. Quedan disponibles en tienda y en urbanutility.mx.
            Con tu portal da seguimiento de tus ventas.
          </p>
          <div className={styles.ctaRow}>
            <Link className={styles.btnPrimary} to={primaryTo}>
              {primaryLabel} →
            </Link>
            {!isAuthenticated && (
              <Link className={styles.btnGhost} to="/login">
                Ya tengo cuenta
              </Link>
            )}
          </div>
        </div>
        <div className={styles.heroVisual}>
          <img className={styles.heroPhoto} src={heroShot} alt="Jumpman Jordan en Urban Utility" />
          <span className={styles.heroCaption}>Urban Utility · GDL</span>
        </div>
      </section>

      <div className={styles.look}>
        <img src={IMAGES.zebra} alt="Yeezy en catálogo Urban" />
        <img src={IMAGES.grail} alt="Grails" />
        <img src={IMAGES.kaws} alt="Coleccionables" />
        <img src={IMAGES.accessories} alt="Accesorios" />
      </div>

      <section className={styles.reach}>
        <div className={styles.reachInner}>
          <p className={styles.kicker} style={{ color: '#8d8a84' }}>
            Dónde se venden
          </p>
          <h2 className={styles.reachTitle}>La gente ya está comprando. Tú pones las piezas.</h2>
          <p className={styles.reachCopy}>
            No armas una audiencia desde cero. Urban ya tiene piso, web y gente que entra a buscar
            sneakers y ropa. Tu inventario se suma a eso.
          </p>
          <div className={styles.channels}>
            <article className={styles.channel}>
              <div className={styles.channelIndex}>01</div>
              <h3 className={styles.channelName}>Tienda</h3>
              <p className={styles.channelNote}>
                Alguien entra, se lo prueba, se lo lleva. Guadalajara, en persona.
              </p>
            </article>
            <article className={styles.channel}>
              <div className={styles.channelIndex}>02</div>
              <h3 className={styles.channelName}>Web</h3>
              <p className={styles.channelNote}>
                Lo pide quien no puede ir al local. Catálogo y envío desde urbanutility.mx.
              </p>
            </article>
            <article className={styles.channel}>
              <div className={styles.channelIndex}>03</div>
              <h3 className={styles.channelName}>POS</h3>
              <p className={styles.channelNote}>
                Se cobra en caja como cualquier otra pieza. Sin trato especial, sin fricción.
              </p>
            </article>
            <article className={styles.channel}>
              <div className={styles.channelIndex}>04</div>
              <h3 className={styles.channelName}>Red</h3>
              <p className={styles.channelNote}>
                Urban tiene una red de seguidores enorme. Obtén exposición a una audiencia enorme.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className={styles.section} id="portal">
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>El portal</h2>
          <p className={styles.sectionAside}>Lo que ves cuando no estás en la tienda.</p>
        </div>
        <div className={styles.features}>
          <article className={styles.feature}>
            <p className={styles.featureLabel}>01 · Inventario</p>
            <h3 className={styles.featureTitle}>Inventario disponible</h3>
            <p className={styles.featureCopy}>
              Pieza, talla, status. Qué está en tienda, qué se fotografió, qué ya se vendió.
            </p>
          </article>
          <article className={styles.feature}>
            <p className={styles.featureLabel}>02 · Ventas</p>
            <h3 className={styles.featureTitle}>Alertas de ventas</h3>
            <p className={styles.featureCopy}>
              Se vende algo y aparece: cuánto, cuándo, por dónde, lo que te toca.
            </p>
          </article>
          <article className={styles.feature}>
            <p className={styles.featureLabel}>03 · Pagos</p>
            <h3 className={styles.featureTitle}>Tú pides el cobro</h3>
            <p className={styles.featureCopy}>
              El monto ya está. Tú eliges cuándo. Nadie tiene que acordarse de transferirte.
            </p>
          </article>
          <article className={styles.feature}>
            <p className={styles.featureLabel}>04 · WhatsApp</p>
            <h3 className={styles.featureTitle}>Notificaciones rápidas</h3>
            <p className={styles.featureCopy}>
              Una venta, un cambio. Te llega al mismo chat de siempre.
            </p>
          </article>
        </div>
      </section>

      <section className={styles.section} id="proceso">
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Cómo</h2>
          <p className={styles.sectionAside}>Así se ve desde adentro.</p>
        </div>
        <div className={styles.steps}>
          <article className={styles.step}>
            <div className={styles.stepShot}>
              <img src={shotHome} alt="Portal Urban: inicio del consignador" />
            </div>
            <div className={styles.stepNum}>01</div>
            <h3 className={styles.stepTitle}>Registramos en tu cuenta</h3>
            <p className={styles.stepCopy}>
              Las recibimos en tienda. Al entrar, ya están cargadas.
            </p>
          </article>
          <article className={styles.step}>
            <div className={styles.stepShot}>
              <img src={shotInventory} alt="Portal Urban: inventario del consignador" />
            </div>
            <div className={styles.stepNum}>02</div>
            <h3 className={styles.stepTitle}>Automáticamente a la venta</h3>
            <p className={styles.stepCopy}>
              Foto, ficha, publicadas. Tú ves cuáles siguen disponibles.
            </p>
          </article>
          <article className={styles.step}>
            <div className={styles.stepShot}>
              <img src={shotSales} alt="Portal Urban: ventas del consignador" />
            </div>
            <div className={styles.stepNum}>03</div>
            <h3 className={styles.stepTitle}>Se vende algo, te enteras</h3>
            <p className={styles.stepCopy}>
              La venta queda registrada. No esperas para saber qué se vendió.
            </p>
          </article>
          <article className={styles.step}>
            <div className={styles.stepShot}>
              <img src={shotPayments} alt="Portal Urban: saldo y solicitud de pago" />
            </div>
            <div className={styles.stepNum}>04</div>
            <h3 className={styles.stepTitle}>Cobras cuando quieras</h3>
            <p className={styles.stepCopy}>
              Solicitas el pago desde ahí. Recibes tu fecha de pago por WhatsApp.
            </p>
          </article>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.audience}>
          <div className={styles.audienceCopy}>
            <p className={styles.kicker}>La tienda</p>
            <h2>
              No es un closet
              <br />
              en Instagram.
            </h2>
            <p>
              Es un local en Guadalajara y una web que ya vende. Staff, vitrina, envíos. Tú pones
              las piezas; el resto ya está armado.
            </p>
          </div>
          <div className={styles.mosaic}>
            <img src={storeShot1} alt="Urban Utility: entrega en tienda" />
            <img src={storeShot2} alt="Urban Utility: revisión de un par" />
            <img src={storeShot3} alt="Urban Utility: wall de sneakers" />
          </div>
        </div>
      </section>

      <section className={styles.closing}>
        <h2>
          Abre tu cuenta.
          <br />
          Nos vemos en el local.
        </h2>
        <p>Si ya consignas con nosotros, entra. Si no, créala y pásate a la tienda.</p>
        <div className={styles.closingCtas}>
          <Link className={styles.btnPrimary} to={primaryTo}>
            {primaryLabel} →
          </Link>
          {!isAuthenticated && (
            <Link className={styles.btnGhost} to="/login">
              Ya tengo cuenta
            </Link>
          )}
        </div>
      </section>

      <footer className={styles.footer}>
        <span>Born To Create</span>
        <span>Guadalajara · {new Date().getFullYear()}</span>
        <a href="https://urbanutility.mx" target="_blank" rel="noreferrer">
          urbanutility.mx
        </a>
      </footer>
    </div>
  );
}

export default ConsignorLanding;
